import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Image as ImageIcon, File, X, Loader2, Search, Filter } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { storage, db } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, query, getDocs, deleteDoc, doc, orderBy } from 'firebase/firestore';

interface MediaFile {
  id: string;
  name: string;
  url: string;
  type: 'image' | 'document';
  size: number;
  uploadedAt: Date;
  storagePath: string;
}

const MediaLibrary = () => {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'gallery' | 'other'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMediaFiles();
  }, []);

  const fetchMediaFiles = async () => {
    try {
      const q = query(collection(db, 'media'), orderBy('uploadedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const files = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt.toDate()
      })) as MediaFile[];
      setMediaFiles(files);
    } catch (error) {
      console.error('Error fetching media files:', error);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    setIsUploading(true);
    try {
      for (const file of acceptedFiles) {
        const timestamp = Date.now();
        const storagePath = `media/${timestamp}_${file.name}`;
        const storageRef = ref(storage, storagePath);
        
        // Upload file to Firebase Storage
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        
        // Add file metadata to Firestore
        const mediaData = {
          name: file.name,
          url: downloadUrl,
          type: file.type.startsWith('image/') ? 'image' : 'document',
          size: file.size,
          uploadedAt: new Date(),
          storagePath: storagePath
        };
        
        await addDoc(collection(db, 'media'), mediaData);
      }
      
      // Refresh the media files list
      await fetchMediaFiles();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Грешка при качване на файловете');
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
      'application/pdf': ['.pdf']
    },
    disabled: isUploading
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDelete = async (id: string, storagePath: string) => {
    try {
      // Delete from Storage
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'media', id));
      
      // Update UI
      setMediaFiles(files => files.filter(file => file.id !== id));
      setSelectedFiles(selected => selected.filter(fileId => fileId !== id));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Грешка при изтриване на файла');
    }
  };

  const toggleFileSelection = (id: string) => {
    setSelectedFiles(selected =>
      selected.includes(id)
        ? selected.filter(fileId => fileId !== id)
        : [...selected, id]
    );
  };

  const deleteSelected = async () => {
    try {
      for (const id of selectedFiles) {
        const file = mediaFiles.find(f => f.id === id);
        if (file) {
          await handleDelete(file.id, file.storagePath);
        }
      }
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error deleting selected files:', error);
      alert('Грешка при изтриване на избраните файлове');
    }
  };

  const isGalleryImage = (fileName: string) => {
    const galleryPatterns = [
      /^molino\d+\.(jpg|jpeg|png|gif)$/i,
      /^logo\.(jpg|jpeg|png|gif)$/i,
      /^hero\.(jpg|jpeg|png|gif)$/i,
      /^melnica\.(jpg|jpeg|png|gif)$/i
    ];
    return galleryPatterns.some(pattern => pattern.test(fileName));
  };

  const filteredFiles = mediaFiles.filter(file => {
    const matchesSearch = searchTerm === '' || 
      file.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'gallery' && isGalleryImage(file.name)) ||
      (filter === 'other' && !isGalleryImage(file.name));
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Медийна библиотека</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Търсене на файлове..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'gallery' | 'other')}
              className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Всички файлове</option>
              <option value="gallery">Галерийни снимки</option>
              <option value="other">Други файлове</option>
            </select>
          </div>
          
          {selectedFiles.length > 0 && (
            <button
              onClick={deleteSelected}
              className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-5 h-5 mr-2" />
              Изтрий избраните ({selectedFiles.length})
            </button>
          )}
          <div className="flex rounded-lg border border-gray-200">
            <button
              onClick={() => setView('grid')}
              className={`px-4 py-2 ${
                view === 'grid'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              } rounded-l-lg transition-colors`}
            >
              Решетка
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 ${
                view === 'list'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              } rounded-r-lg transition-colors`}
            >
              Списък
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Всички файлове</p>
              <p className="text-2xl font-bold text-gray-900">{mediaFiles.length}</p>
            </div>
            <File className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Галерийни снимки</p>
              <p className="text-2xl font-bold text-blue-600">
                {mediaFiles.filter(f => isGalleryImage(f.name)).length}
              </p>
            </div>
            <ImageIcon className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Други файлове</p>
              <p className="text-2xl font-bold text-green-600">
                {mediaFiles.filter(f => !isGalleryImage(f.name)).length}
              </p>
            </div>
            <Upload className="w-8 h-8 text-green-400" />
          </div>
        </div>
      </div>
      <div
        {...getRootProps()}
        className={`mb-6 border-2 border-dashed rounded-lg p-8 text-center ${
          isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300'
        } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        {isUploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
            <p className="text-gray-600">Качване на файлове...</p>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {isDragActive
                ? 'Пуснете файловете тук...'
                : 'Плъзнете файлове тук или кликнете, за да изберете'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Поддържани формати: JPG, PNG, GIF, PDF
            </p>
          </>
        )}
      </div>

      {view === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {filteredFiles.map(file => (
            <div
              key={file.id}
              className={`relative group rounded-lg border ${
                selectedFiles.includes(file.id)
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200'
              } ${isGalleryImage(file.name) ? 'ring-2 ring-blue-200' : ''}`}
            >
              {isGalleryImage(file.name) && (
                <div className="absolute top-1 left-1 z-10">
                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    Галерия
                  </span>
                </div>
              )}
              <div className="aspect-square p-4">
                {file.type === 'image' ? (
                  <img
                    src={file.url}
                    alt={file.name}
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded">
                    <File className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="p-3 border-t border-gray-200">
                <p className="text-sm font-medium truncate" title={file.name}>
                  {file.name}
                </p>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              </div>
              <button
                onClick={() => toggleFileSelection(file.id)}
                className={`absolute top-2 right-2 p-1 rounded-full ${
                  selectedFiles.includes(file.id)
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-500 opacity-0 group-hover:opacity-100'
                } transition-opacity`}
              >
                {selectedFiles.includes(file.id) ? (
                  <X className="w-4 h-4" />
                ) : (
                  <ImageIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Файл
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Тип
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Размер
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Качен на
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFiles.map(file => (
                <tr
                  key={file.id}
                  className={`${selectedFiles.includes(file.id) ? 'bg-primary/5' : ''} ${
                    isGalleryImage(file.name) ? 'bg-blue-50' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {isGalleryImage(file.name) && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full mr-2">
                          Галерия
                        </span>
                      )}
                      {file.type === 'image' ? (
                        <ImageIcon className="w-5 h-5 text-gray-400 mr-3" />
                      ) : (
                        <File className="w-5 h-5 text-gray-400 mr-3" />
                      )}
                      <span className="text-sm text-gray-900">{file.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">
                      {file.type === 'image' ? 'Изображение' : 'Документ'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">
                      {formatFileSize(file.size)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">
                      {file.uploadedAt.toLocaleDateString('bg-BG')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleDelete(file.id, file.storagePath)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {filteredFiles.length === 0 && !loading && (
        <div className="text-center py-12">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filter !== 'all' ? 'Няма намерени файлове' : 'Няма качени файлове'}
          </h3>
          <p className="text-gray-500">
            {searchTerm || filter !== 'all' 
              ? 'Опитайте с различни критерии за търсене или филтър'
              : 'Качете първите си файлове, за да започнете'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;